import { CalendarIconFilled, GoogleLogoIcon, WarningIcon } from '@systemeio/ui/icons';
import { Checkbox, DangerButton, Hint, PrimaryButton, toast } from '@systemeio/ui';
import { Fragment, useState } from 'react';
import ConfirmationModal from 'shared/components/confirmation-modal';
import { DisclosureBox } from 'shared/components/disclosure-box';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useModal } from 'shared/hooks/use-modal';
import useUser from 'shared/hooks/use-user';
import { dateFormat } from 'shared/utils/date-time-format';
import { twMerge } from 'tailwind-merge';
import { GoogleCalendarSelector } from './calendar-selector';
import { useCalendars } from './hooks/use-calendars';
import { useGoogleIntegrations } from './hooks/use-google-integrations';
import { CalendarInterface, IntegrationInterface, IntegrationUseCaseEnum } from './types';
import { hasMultipleUseCases } from './utils/has-multiple-use-cases';
export const GoogleCalendarConfiguration = ({
  onAddAccount,
  onReconnect
}: {
  onAddAccount: () => void;
  onReconnect: (id: IntegrationInterface['id'], email: string) => Promise<void>;
}) => {
  const {
    user
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  const [deletingIntegrations, setDeletingIntegrations] = useState<string[]>([]);
  const [changingCalendarsUsage, setChangingCalendarsUsage] = useState<string[]>([]);
  const [settingPrimaryCalendar, setSettingPrimaryCalendar] = useState(false);
  const [reconnectingIntegrations, setReconnectingIntegrations] = useState<string[]>([]);
  const removeModal = useModal<IntegrationInterface>(false, false);
  const {
    data: calendars,
    patchCalendar,
    mutate: mutateCalendars
  } = useCalendars();
  const {
    getIntegrationsByUseCase,
    mutate,
    removeIntegration
  } = useGoogleIntegrations();
  const integrations = getIntegrationsByUseCase(IntegrationUseCaseEnum.Calendar);
  async function handleReconnect(item: IntegrationInterface) {
    try {
      setReconnectingIntegrations(prev => [...prev, item.id]);
      await onReconnect(item.id, item.googleUserEmail);
    } catch {} finally {
      setReconnectingIntegrations(prev => prev.filter(id => id !== item.id));
    }
  }
  async function handleRemove(item_0: IntegrationInterface) {
    if (hasMultipleUseCases(item_0) && !removeModal.opened) {
      removeModal.open(item_0);
      return;
    }
    try {
      setDeletingIntegrations(prev_0 => [...prev_0, item_0.id]);
      await removeIntegration(item_0.id);
      await mutate();
      toast.success(t('dashboard.user_settings.integrations_page.disconnected'));
    } catch {} finally {
      setDeletingIntegrations(prev => prev.filter(id => id !== item_0.id));
    }
  }
  async function handleSetCalendarUsage(calendar: CalendarInterface, checked: boolean) {
    try {
      setChangingCalendarsUsage(prev_1 => [...prev_1, calendar.id]);
      await patchCalendar(calendar.id, calendar.integration.id, {
        useForCheck: checked
      });
      await mutateCalendars(data => {
        if (!data) return;
        return data.map(c => {
          if (calendar.id === c.id && calendar.integration.id === c.integration.id) {
            return {
              ...c,
              isUsedForCheck: checked
            };
          }
          return c;
        });
      }, false);
      toast.success(t('global.changes_saved'));
    } catch {} finally {
      setChangingCalendarsUsage(prev => prev.filter(id => id !== calendar.id));
    }
  }
  async function handleSetDefaultCalendar(calendarId?: string, integrationId?: string) {
    if (!calendarId || !integrationId) return;
    try {
      setSettingPrimaryCalendar(true);
      await patchCalendar(calendarId, integrationId, {
        useForSync: true
      });
      await mutateCalendars(data_0 => {
        if (!data_0) return;
        return data_0.map(c_0 => {
          if (calendarId === c_0.id && integrationId === c_0.integration.id) {
            return {
              ...c_0,
              isUsedForSync: true
            };
          }
          return {
            ...c_0,
            isUsedForSync: false
          };
        });
      }, false);
      toast.success(t('global.changes_saved'));
    } catch {} finally {
      setSettingPrimaryCalendar(false);
    }
  }
  const hasIntegrations = integrations?.items && integrations.items.length > 0;
  const hasCalendars = calendars && calendars.length > 0;
  return <div className="flex flex-col gap-5">
      <p>{t('dashboard.user_settings.integrations_page.google_calendar.description')}</p>

      <PrimaryButton className="w-full" onClick={onAddAccount}>
        {t('dashboard.user_settings.integrations_page.google_calendar.add_account')}
      </PrimaryButton>

      {integrations?.items?.length && <div className="flex flex-col gap-1">
          <span className="text-text-primary text-base font-bold">
            {t('dashboard.user_settings.integrations_page.google_calendar.connected_accounts')}:
          </span>
          <div className="border-fill-primary bg-fill-primary/10 gap-5 rounded-lg border p-6">
            {integrations.items.map(integration => {
          const isDisconnected = !!integration.disconnectedAt;
          return <div key={integration.id} className="border-fill-primary/50 mb-5 flex flex-col gap-3 border-b pb-5 last:mb-0 last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2.5">
                    {isDisconnected ? <WarningIcon className="fill-yellow h-[25px] w-[25px] shrink-0" /> : <GoogleLogoIcon className="shrink-0" />}
                    <div className="flex w-full flex-col truncate">
                      <span className={twMerge('text-text-primary truncate text-base font-semibold', isDisconnected && 'text-text-primary/70')}>
                        {integration.googleUserEmail}
                      </span>
                      <span className="text-text-tertiary truncate">
                        {dateFormat(integration.createdAt, {
                    dateFormat: user?.dateFormat,
                    timeZone: user?.timezone
                  })}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-row-reverse flex-wrap justify-end gap-3 sm:flex-row">
                    {isDisconnected && <PrimaryButton className="!h-9 min-h-0" isFetching={reconnectingIntegrations.includes(integration.id)} onClick={() => handleReconnect(integration)}>
                        {t('dashboard.user_settings.integrations_page.reconnect')}
                      </PrimaryButton>}

                    <DangerButton className="!h-9 min-h-0" isFetching={deletingIntegrations.includes(integration.id)} onClick={() => handleRemove(integration)}>
                      {t('dashboard.user_settings.integrations_page.disconnect')}
                    </DangerButton>
                  </div>
                </div>;
        })}
          </div>
        </div>}
      <ConfirmationModal afterLeave={() => removeModal.cleanData()} opened={removeModal.opened} onClose={() => removeModal.close()} onConfirm={() => removeModal.data && handleRemove(removeModal.data)} confirmationText={<TransLoco t={t} i18nKey="dashboard.user_settings.integrations_page.google_calendar.delete_confirmation" values={{
      account_address: removeModal.data?.googleUserEmail
    }} components={{
      b: <b />
    }} />} />
      {hasCalendars && <GoogleCalendarSelector label={<Hint label={t('dashboard.user_settings.integrations_page.google_calendar.default_calendar_tooltip')}>
              {t('dashboard.user_settings.integrations_page.google_calendar.default_calendar')}
            </Hint>} disabled={settingPrimaryCalendar} calendar={calendars?.find(calendar_0 => calendar_0.isUsedForSync)} onChange={handleSetDefaultCalendar} />}

      {hasIntegrations && hasCalendars && <div className="flex flex-col gap-1">
          <Hint label={t('dashboard.user_settings.integrations_page.google_calendar.check_for_conflicts_tooltip')}>
            {t('dashboard.user_settings.integrations_page.google_calendar.check_for_conflicts')}
          </Hint>
          <div className="border-fill-primary rounded-lg border">
            {integrations.items.filter(integration_0 => integration_0.disconnectedAt === null).map((integration_1, index) => {
          const isLast = index === integrations.items.length - 1;
          const integrationCalendars = calendars.filter(calendar_1 => calendar_1.integration.id === integration_1.id);
          return <Fragment key={integration_1.id}>
                    <DisclosureBox buttonClassName="border-0" panelClassName="flex flex-col gap-5 pt-0" title={<span className="text-text-primary truncate text-base">
                          {integration_1.googleUserEmail}
                        </span>}>
                      {integrationCalendars.map(calendar_2 => {
                return <div key={calendar_2.id} className={twMerge('flex flex-col gap-5', changingCalendarsUsage.includes(calendar_2.id) && 'animate-pulse')}>
                            <Checkbox labelClassName="truncate" className="w-full" value={calendar_2.isUsedForCheck} label={<div className="flex w-full items-center gap-4 truncate">
                                  <CalendarIconFilled className="shrink-0" />
                                  <span className="truncate">{calendar_2.name}</span>
                                </div>} isFetching={changingCalendarsUsage.includes(calendar_2.id)} onChange={value => handleSetCalendarUsage(calendar_2, value)} />
                          </div>;
              })}
                    </DisclosureBox>
                    {!isLast && <div className="border-fill-primary/50 mx-5 border-b" />}
                  </Fragment>;
        })}
          </div>
        </div>}
    </div>;
};